/**
 * RecurrencePicker (Workspace v3.x) — the Repeats picker, extracted from the
 * HubMyTasks quick-add so the drawer "Edit repeat" reuses the SAME control. Controlled
 * (the parent owns the RecurrenceDraft); the cadence→anchors mapping + the readiness
 * gate are pure exports (one source for create AND update payloads). `idPrefix`
 * namespaces the ids + testids so two instances never collide (`hub` for quick-add,
 * `drawer` for the edit).
 *
 * Out of scope here: the one-off Due-date field (a dated one-off is a plain task, not
 * a series) — that stays in the parent and shows only when cadence is ''.
 */

import { type ReactNode } from 'react';
import type { RecurrenceCadence, RecurrenceBusinessDayRule, RecurringSeriesRow } from '@/lib/api';
import { SelectField } from '@/components/primitives/SelectField';
import { DateInput } from '@/components/primitives/DateInput';

export const PICKER_FIELD =
  'rounded-base border border-sage-200 bg-paper px-2 py-1.5 text-[13px] text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400';

/** Cadence options ('' = a one-off task, the default). `specific_date` is intentionally
 *  NOT offered — a dated one-off is a plain task with a due_date, not a series. */
const CADENCE_OPTIONS: { value: RecurrenceCadence | ''; label: string }[] = [
  { value: '', label: 'Does not repeat' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'every_two_weeks', label: 'Every 2 weeks' },
  { value: 'twice_monthly', label: 'Twice a month' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'last_business_day', label: 'Last business day' },
  { value: 'week_of', label: 'Week of…' },
];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const BUSINESS_DAY_OPTIONS: { value: RecurrenceBusinessDayRule; label: string }[] = [
  { value: 'none', label: 'Use the exact date' },
  { value: 'previous_business_day', label: 'Move earlier off weekends' },
  { value: 'next_business_day', label: 'Move later off weekends' },
];

export interface RecurrenceDraft {
  cadence: RecurrenceCadence | '';
  dow: number; // weekly: 0..6 (default Friday)
  dom1: number; // monthly / week_of / twice_monthly (1st)
  dom2: number; // twice_monthly (2nd)
  anchorDate: string; // every_two_weeks / specific_date
  bdr: RecurrenceBusinessDayRule;
}

export const EMPTY_RECURRENCE: RecurrenceDraft = { cadence: '', dow: 5, dom1: 1, dom2: 15, anchorDate: '', bdr: 'none' };

/** Seed the Edit picker from an existing rule (the WorkItemRow carries only the summary
 *  string, so the drawer GETs the rule and maps it here). */
export function draftFromSeries(s: RecurringSeriesRow): RecurrenceDraft {
  return {
    cadence: s.cadence,
    dow: s.anchor_dow ?? 5,
    dom1: s.anchor_dom ?? s.week_of_dom ?? 1,
    dom2: s.anchor_dom_2 ?? 15,
    anchorDate: s.anchor_date ?? '',
    bdr: s.business_day_rule,
  };
}

/** The chosen cadence's required anchor is filled in (a client gate; the server
 *  re-validates ranges + twice_monthly's dom_2 > dom_1). */
export function recurrenceAnchorReady(d: RecurrenceDraft): boolean {
  switch (d.cadence) {
    case '':
    case 'weekly':
    case 'last_business_day':
      return true;
    case 'every_two_weeks':
    case 'specific_date':
      return d.anchorDate !== '';
    case 'monthly':
    case 'week_of':
      return d.dom1 >= 1 && d.dom1 <= 31;
    case 'twice_monthly':
      return d.dom1 >= 1 && d.dom2 >= 1 && d.dom2 > d.dom1 && d.dom2 <= 31;
    default:
      return false;
  }
}

export interface RecurrenceAnchorPayload {
  anchor_dow?: number;
  anchor_dom?: number;
  anchor_dom_2?: number;
  anchor_date?: string;
  week_of_dom?: number;
  business_day_rule: RecurrenceBusinessDayRule;
}

/** cadence → the anchor fields for a create OR update payload (one source). Call only
 *  when cadence is non-empty (a '' draft is a one-off, not a series). */
export function recurrenceAnchors(d: RecurrenceDraft): RecurrenceAnchorPayload {
  const base: RecurrenceAnchorPayload = { business_day_rule: d.bdr };
  switch (d.cadence) {
    case 'weekly':
      return { ...base, anchor_dow: d.dow };
    case 'every_two_weeks':
    case 'specific_date':
      return { ...base, anchor_date: d.anchorDate };
    case 'monthly':
      return { ...base, anchor_dom: d.dom1 };
    case 'week_of':
      return { ...base, week_of_dom: d.dom1 };
    case 'twice_monthly':
      return { ...base, anchor_dom: d.dom1, anchor_dom_2: d.dom2 };
    default:
      return base; // last_business_day (no anchor)
  }
}

export function RecurrencePicker({
  value,
  onChange,
  idPrefix,
  labelClassName,
}: {
  value: RecurrenceDraft;
  onChange: (next: RecurrenceDraft) => void;
  /** 'hub' (quick-add) | 'drawer' (edit) — namespaces ids + testids. */
  idPrefix: string;
  /** Extra classes for the Repeats label (the add form lines its labels up). */
  labelClassName?: string;
}): ReactNode {
  const set = (patch: Partial<RecurrenceDraft>): void => onChange({ ...value, ...patch });
  const tid = (s: string): string => `${idPrefix}-repeats-${s}`;
  return (
    <>
      <label htmlFor={`${idPrefix}-repeats`} className={`text-[13px] text-ink-soft ${labelClassName ?? ''}`}>Repeats</label>
      <SelectField size="compact"
        id={`${idPrefix}-repeats`}
        data-testid={tid('cadence')}
        value={value.cadence}
        onChange={(e) => set({ cadence: e.target.value as RecurrenceCadence | '' })}
      >
        {CADENCE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </SelectField>

      {value.cadence === 'weekly' && (
        <SelectField size="compact" data-testid={tid('dow')} aria-label="Weekday" value={value.dow} onChange={(e) => set({ dow: Number(e.target.value) })}>
          {WEEKDAYS.map((d, i) => (
            <option key={d} value={i}>{d}</option>
          ))}
        </SelectField>
      )}
      {(value.cadence === 'every_two_weeks' || value.cadence === 'specific_date') && (
        <DateInput
          data-testid={tid('date')}
          aria-label={value.cadence === 'every_two_weeks' ? 'Starting date' : 'Date'}
          value={value.anchorDate}
          onChange={(e) => set({ anchorDate: e.target.value })}
          size="compact"
        />
      )}
      {(value.cadence === 'monthly' || value.cadence === 'week_of') && (
        <input
          type="number"
          min={1}
          max={31}
          data-testid={tid('dom')}
          aria-label="Day of the month"
          value={value.dom1}
          onChange={(e) => set({ dom1: Number(e.target.value) })}
          className={PICKER_FIELD + ' w-16'}
        />
      )}
      {value.cadence === 'twice_monthly' && (
        <>
          <input type="number" min={1} max={31} data-testid={tid('dom')} aria-label="First day of the month" value={value.dom1} onChange={(e) => set({ dom1: Number(e.target.value) })} className={PICKER_FIELD + ' w-16'} />
          <span className="text-[13px] text-ink-soft">and</span>
          <input type="number" min={1} max={31} data-testid={tid('dom2')} aria-label="Second day of the month" value={value.dom2} onChange={(e) => set({ dom2: Number(e.target.value) })} className={PICKER_FIELD + ' w-16'} />
        </>
      )}
      {value.cadence !== '' && (
        <SelectField size="compact" data-testid={tid('bdr')} aria-label="Weekend handling" value={value.bdr} onChange={(e) => set({ bdr: e.target.value as RecurrenceBusinessDayRule })}>
          {BUSINESS_DAY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </SelectField>
      )}
    </>
  );
}
